import { Button } from "@kopi/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@kopi/ui/components/dropdown-menu";
import { cn } from "@kopi/ui/lib/utils";
import { ChevronDownIcon, MonitorIcon, MoonIcon, SunIcon } from "lucide-react";
import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { PlainMessageKey } from "@/i18n/messages/id";
import { useI18n } from "@/i18n/provider";
import { browserStorage } from "@/lib/storage";
import {
  type EffectiveTheme,
  effectiveTheme,
  isThemeChoice,
  readThemeChoice,
  THEME_CHOICES,
  THEME_COLOR,
  type ThemeChoice,
  writeThemeChoice,
} from "@/lib/theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * Loaded after first paint, off the cashier's critical path (PRD §5): screens that toast import
 * `toast` themselves, and a toast only follows a user action, by which time the toaster is mounted.
 */
const Toaster = lazy(() => import("@kopi/ui/components/sonner").then((m) => ({ default: m.Toaster })));

interface ThemeState {
  choice: ThemeChoice;
  effective: EffectiveTheme;
  setChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function useTheme(): ThemeState {
  const state = useContext(ThemeContext);
  if (!state) throw new Error("useTheme must be used inside <ThemeProvider>");
  return state;
}

/**
 * Owns the Terang/Gelap/Sistem choice (CODING_STANDARDS §2). The first paint is already right: the
 * inline script in index.html sets the `dark` class before React loads; this keeps it in step,
 * follows the OS live while the choice is "system", and mounts the themed toaster once.
 */
export function ThemeProvider(props: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readThemeChoice(browserStorage()));
  const [systemDark, setSystemDark] = useState(() => window.matchMedia(DARK_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const effective = effectiveTheme(choice, systemDark);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", effective === "dark");
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[effective]);
  }, [effective]);

  const value = useMemo<ThemeState>(
    () => ({
      choice,
      effective,
      setChoice: (next) => {
        writeThemeChoice(browserStorage(), next);
        setChoiceState(next);
      },
    }),
    [choice, effective],
  );

  return (
    <ThemeContext.Provider value={value}>
      {props.children}
      <Suspense fallback={null}>
        <Toaster theme={effective} position="top-center" />
      </Suspense>
    </ThemeContext.Provider>
  );
}

const LABELS: Record<ThemeChoice, PlainMessageKey> = {
  light: "theme.light",
  dark: "theme.dark",
  system: "theme.system",
};

const CHOICE_ICONS: Record<ThemeChoice, ReactNode> = {
  light: <SunIcon className="size-4" />,
  dark: <MoonIcon className="size-4" />,
  system: <MonitorIcon className="size-4" />,
};

/** Sun/moon button opening the Terang / Gelap / Sistem menu. */
export function ThemePicker(props: { className?: string }) {
  const { choice, effective } = useTheme();
  const { t } = useI18n();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn("gap-1.5 bg-card px-3 shadow-xs", props.className)}
          aria-label={t("theme.current", { choice: t(LABELS[choice]) })}
        >
          {effective === "dark" ? (
            <MoonIcon className="size-4 text-muted-foreground" />
          ) : (
            <SunIcon className="size-4 text-muted-foreground" />
          )}
          <span className="font-medium text-xs">{t(LABELS[choice])}</span>
          <ChevronDownIcon className="size-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <ThemeMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "Tema" label + Terang / Gelap / Sistem radio items, for any `DropdownMenuContent` (the user menu). */
export function ThemeMenuItems() {
  const { choice, setChoice } = useTheme();
  const { t } = useI18n();
  return (
    <>
      <DropdownMenuLabel className="text-muted-foreground text-xs">{t("theme.heading")}</DropdownMenuLabel>
      <DropdownMenuRadioGroup value={choice} onValueChange={(next) => isThemeChoice(next) && setChoice(next)}>
        {THEME_CHOICES.map((option) => (
          <DropdownMenuRadioItem key={option} value={option} className="min-h-11 justify-between">
            <span>{t(LABELS[option])}</span>
            <span className="text-muted-foreground group-data-[state=checked]/radio-item:text-primary">
              {CHOICE_ICONS[option]}
            </span>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  );
}
